/**
 * Bibliotheque.jsx — Le hub unique de tout le contenu pédagogique.
 *
 * Accessible aux élèves ET aux enseignants (même URL, UI adaptée) :
 *  - Élèves : bouton "Ouvrir" par cours
 *  - Enseignants (via useTeacherMode) : bouton "Ouvrir" + bouton "QR" pour partager
 *
 * Layout :
 *  - Hero avec 5 grandes cartes (Permis AM · Plateau · Circulation · 125 · Briefings)
 *  - En dessous : chaque catégorie déroulée avec sa liste de cours (compact)
 *  - Mobile-first : 1 colonne, boutons ≥44px
 *  - Route : /bibliotheque
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Share2, X, Copy, Check, MessageCircle, QrCode, Search,
  ArrowRight, BookOpen,
} from 'lucide-react';
import { BIBLIOTHEQUE_CATALOG, TOTAL_CONTENUS } from '../data/bibliothequeCatalog';
import { useTeacherMode } from '../hooks/useTeacherMode';

const SHARE_TOKEN = 'c2r';

// ============================================================
// Modale QR — utilisée uniquement en mode enseignant.
// ============================================================
function QRModal({ course, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!course) return null;
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = `${baseUrl}${course.path}?share=${SHARE_TOKEN}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) { /* ignore */ }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`📚 Voici le cours à consulter : ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="biblio-qr-modal"
    >
      <div
        className="bg-white rounded-3xl p-6 sm:p-7 max-w-sm w-full shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center active:scale-95 transition"
          aria-label="Fermer"
          data-testid="biblio-qr-close"
        >
          <X className="w-4 h-4 text-slate-700" strokeWidth={2.5} />
        </button>

        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-600 text-[10px] font-black uppercase tracking-widest mb-2">
            <Share2 className="w-3 h-3" /> Partage moniteur
          </div>
          <h2
            className="text-lg sm:text-xl font-black text-slate-900 leading-tight px-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {course.label}
          </h2>
          <p className="text-[11px] text-slate-500 mt-2">
            Tes élèves scannent le QR · aucun compte requis
          </p>
        </div>

        <div className="bg-white rounded-2xl p-4 border-2 border-slate-100 flex items-center justify-center mb-4">
          <QRCodeSVG
            value={shareUrl}
            size={220}
            level="M"
            includeMargin={false}
            fgColor="#0f172a"
            bgColor="#ffffff"
          />
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5 mb-3 flex items-center gap-2">
          <div className="flex-1 min-w-0 text-[10px] text-slate-600 font-mono truncate select-all">
            {shareUrl}
          </div>
          <button
            type="button"
            onClick={copyLink}
            className={`flex-shrink-0 inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest transition ${
              copied ? 'bg-emerald-500 text-white' : 'bg-slate-900 text-white hover:bg-slate-700 active:scale-95'
            }`}
          >
            {copied ? <><Check className="w-3 h-3" /> Copié</> : <><Copy className="w-3 h-3" /> Copier</>}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={shareWhatsApp}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-black uppercase tracking-widest text-[10px] px-3 py-3 active:scale-95 transition min-h-[44px]"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </button>
          <Link
            to={course.path}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 hover:bg-slate-700 text-white font-black uppercase tracking-widest text-[10px] px-3 py-3 active:scale-95 transition min-h-[44px]"
          >
            Ouvrir le cours
          </Link>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Grande carte catégorie (héros du haut) — grand format, tactile.
// ============================================================
function CategoryHeroCard({ category, onClick }) {
  const Icon = category.icon;
  const bgStyle = {
    backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.85) 100%), ${category.coverFallback}`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative overflow-hidden rounded-3xl min-h-[180px] sm:min-h-[220px] p-5 sm:p-6 text-left ring-1 ${category.ring} hover:ring-2 active:scale-[0.98] transition-all shadow-xl`}
      style={bgStyle}
      data-testid={`biblio-cat-hero-${category.key}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${category.accent} opacity-70 pointer-events-none`} />
      <div className="relative z-10 h-full flex flex-col justify-between gap-3">
        <div className="flex items-start justify-between">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.2} />
          </div>
          <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${category.badge}`}>
            {category.courses.length} contenus
          </span>
        </div>
        <div>
          <div className="text-white/70 text-[10.5px] uppercase tracking-[0.22em] font-bold mb-1.5">{category.subtitle}</div>
          <h3
            className="text-2xl sm:text-3xl font-black text-white leading-tight drop-shadow-lg mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {category.title}
          </h3>
          <p className="text-white/85 text-[12.5px] leading-snug line-clamp-2">{category.tagline}</p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-white text-[11px] font-black uppercase tracking-widest">
            Explorer <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </button>
  );
}

// ============================================================
// Composant principal.
// ============================================================
export default function Bibliotheque() {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [openCat, setOpenCat] = useState(null); // catégorie développée sur mobile
  const { teacherMode } = useTeacherMode();
  const navigate = useNavigate();
  const location = useLocation();
  const sectionsRef = useRef({});

  // Auto-scroll vers la catégorie ciblée via le hash de l'URL
  // (ex. /bibliotheque#plateau-a2 → scroll vers la section Plateau)
  useEffect(() => {
    if (!location.hash) return;
    const key = location.hash.replace(/^#/, '');
    // Petit délai pour laisser le DOM se stabiliser + rendu des sections
    const timer = setTimeout(() => {
      const node = sectionsRef.current[key];
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [location.hash]);

  const filteredCatalogue = useMemo(() => {
    if (!query.trim()) return BIBLIOTHEQUE_CATALOG;
    const q = query.toLowerCase();
    return BIBLIOTHEQUE_CATALOG
      .map((cat) => ({
        ...cat,
        courses: cat.courses.filter(
          (c) => c.label.toLowerCase().includes(q) || cat.title.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.courses.length > 0);
  }, [query]);

  const scrollToCategory = (key) => {
    const node = sectionsRef.current[key];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setOpenCat(key);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 sm:space-y-10 px-2 sm:px-0" data-testid="bibliotheque-page">
      {/* ============= HERO EN-TÊTE ============= */}
      <header className="rounded-3xl bg-gradient-to-br from-[#1a0f14] via-[#0f0f10] to-[#0d0d0e] border border-white/10 p-5 sm:p-8 shadow-xl">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10px] font-black uppercase tracking-widest">
            <BookOpen className="w-3 h-3" /> Ma bibliothèque
          </div>
          <span className="text-white/40 text-[11px] font-mono">{TOTAL_CONTENUS} contenus · 5 catégories</span>
          {teacherMode && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-black uppercase tracking-widest">
              <QrCode className="w-3 h-3" /> Mode enseignant · QR actifs
            </span>
          )}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-black text-white leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {teacherMode ? 'Tous mes cours à projeter et partager' : 'Tout mon contenu de formation, au même endroit'}
        </h1>
        <p className="text-white/70 text-sm sm:text-base mt-2 max-w-2xl leading-relaxed">
          Sélectionne une catégorie ci-dessous pour accéder à tes cours.
          {teacherMode && ' Un bouton QR à côté de chaque cours permet de le projeter en classe pour un accès instantané des élèves.'}
        </p>
      </header>

      {/* ============= 5 CARTES CATÉGORIES ============= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {BIBLIOTHEQUE_CATALOG.map((cat) => (
          <CategoryHeroCard key={cat.key} category={cat} onClick={() => scrollToCategory(cat.key)} />
        ))}
      </div>

      {/* ============= BARRE DE RECHERCHE ============= */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un cours (ex. plateau, AM, freinage)…"
          className="w-full h-12 pl-11 pr-4 rounded-2xl bg-[#141416] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20 transition"
          data-testid="bibliotheque-search"
        />
      </div>

      {/* ============= LISTES DÉTAILLÉES PAR CATÉGORIE ============= */}
      {filteredCatalogue.length === 0 ? (
        <div className="rounded-2xl bg-[#141416] border border-white/10 p-8 text-center text-white/50 text-sm">
          Aucun cours ne correspond à ta recherche.
        </div>
      ) : (
        filteredCatalogue.map((cat) => {
          const CatIcon = cat.icon;
          return (
            <section
              key={cat.key}
              ref={(el) => { sectionsRef.current[cat.key] = el; }}
              className="scroll-mt-24 space-y-3"
              data-testid={`biblio-section-${cat.key}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${cat.badge} border`}>
                  <CatIcon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white/40 text-[10px] font-mono uppercase tracking-widest">{cat.subtitle}</div>
                  <h2
                    className="text-xl sm:text-2xl font-black text-white leading-tight"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {cat.title}
                  </h2>
                </div>
                <span className="text-white/40 text-[11px] font-mono shrink-0">{cat.courses.length}</span>
              </div>

              {/* Liste des cours — mobile-first, ≥44px tap targets */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {cat.courses.map((c) => (
                  <div
                    key={c.path}
                    className="group flex items-center gap-3 p-3 sm:p-3.5 rounded-2xl bg-[#141416] border border-white/10 hover:border-white/20 hover:bg-[#1a1a1c] transition"
                    data-testid={`biblio-course-${c.path.replace(/\//g, '-')}`}
                  >
                    <div className={`shrink-0 w-2 h-10 sm:h-11 rounded-full ${cat.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-white text-[13.5px] font-semibold leading-tight">{c.label}</div>
                        {c.badge && (
                          <span className="shrink-0 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            {c.badge}
                          </span>
                        )}
                      </div>
                      <div className="text-white/40 text-[11px] mt-0.5 font-mono">{c.duration}</div>
                    </div>
                    {/* Bouton "Ouvrir" — toujours visible */}
                    <button
                      type="button"
                      onClick={() => navigate(c.path)}
                      className="shrink-0 inline-flex items-center gap-1 min-h-[44px] px-3 sm:px-3.5 rounded-xl border border-white/15 bg-white/5 text-white hover:bg-white/10 hover:border-white/25 text-[11.5px] font-semibold active:scale-95 transition"
                      data-testid={`biblio-open-${c.path.replace(/\//g, '-')}`}
                    >
                      Ouvrir <ArrowRight className="w-3 h-3" />
                    </button>
                    {/* Bouton "QR" — uniquement en mode enseignant */}
                    {teacherMode && (
                      <button
                        type="button"
                        onClick={() => setSelected(c)}
                        className="shrink-0 inline-flex items-center gap-1.5 min-h-[44px] px-3 sm:px-3.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-black uppercase tracking-widest shadow-lg shadow-rose-900/40 active:scale-95 transition"
                        data-testid={`biblio-qr-${c.path.replace(/\//g, '-')}`}
                        aria-label={`Partager ${c.label} via QR`}
                      >
                        <QrCode className="w-3.5 h-3.5" /> QR
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })
      )}

      {/* ============= INFO PIED ============= */}
      {teacherMode && (
        <div className="rounded-2xl bg-blue-500/5 border border-blue-500/20 p-4 text-[12px] text-blue-200 leading-relaxed">
          <strong className="text-blue-300">💡 Comment ça marche ?</strong> Chaque QR encode l&apos;URL du cours avec un jeton
          temporaire <code className="text-[11px] px-1.5 py-0.5 rounded bg-white/10">?share=c2r</code> qui autorise
          un accès session (tant que l&apos;élève garde son onglet ouvert). Idéal pour projeter en classe.
        </div>
      )}

      <QRModal course={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
